import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material"
import ShieldIcon from "@mui/icons-material/AdminPanelSettingsOutlined"

import { roleApi } from "../api"
import { usePermissions } from "../permissions"
import PageHeader from "../components/PageHeader"
import EmptyState from "../components/EmptyState"
import { LoadingRows, LoadingScreen } from "../components/LoadingState"
import { MODULE_LABELS } from "../permissions/manifest"
import { titleCase } from "../utils/format"

const GROUP_ORDER = ["crud", "data", "people", "admin", "diagnostics", "management"]
const GROUP_LABELS = {
  crud: "Basics",
  data: "Data",
  people: "People",
  admin: "Administration",
  diagnostics: "Diagnostics",
  management: "Management",
}

/*
 * What a box actually lets somebody do, where the action's name does not say.
 *
 * Shown under the label and in its tooltip. The Users module is the one where
 * two names sit next to each other and mean different things — "Reset
 * Password" emails a link, "Set Password" types one in — and an administrator
 * granting the wrong one has handed out the stronger power by accident.
 */
const ACTION_HELP = {
  users: {
    view: "See people and open their page",
    create: "Add people and send their invite",
    update: "Edit name, email, title, phone and department",
    delete: "Remove someone from the list (same as deactivating)",
    export: "Download the people list as a spreadsheet",
    assign_role: "Change what someone is allowed to do",
    reset_password: "Email a link to set or reset their password",
    set_password: "Type a password for them directly — break-glass",
    change_username: "Change the name they sign in with",
    deactivate: "Turn an account off, and back on",
    manage_sessions: "See their devices and sign-ins, and sign them out",
    view_activity: "See what they have been doing, and their account history",
  },
}

/**
 * Role and permission management.
 *
 * ── The catalogue comes from the server ─────────────────────────────────────
 *
 * `GET /roles/catalog?audience=` returns the modules and actions the server
 * actually understands, filtered for the audience. The client has its own bit map
 * in `permissions/manifest.js`, but the EDITOR deliberately does not use it: two
 * copies drift, and a drifted editor offers a permission that silently means
 * something else, or one the server strips on save without saying so.
 *
 * ── Saving signs people out ─────────────────────────────────────────────────
 *
 * The server revokes the sessions of everyone holding the role, because the
 * client renders its navigation from the grants it got at login. That is stated
 * on the button rather than discovered afterwards.
 */
export default function RolesPage() {
  const { can } = usePermissions()
  const [audience, setAudience] = useState("back_office")
  const [roles, setRoles] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [detail, setDetail] = useState(null)
  const [catalog, setCatalog] = useState([])
  const [draft, setDraft] = useState({})
  const [loading, setLoading] = useState(true)
  const [detailLoading, setDetailLoading] = useState(false)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(null)
  const [busy, setBusy] = useState(false)

  const loadRoles = useCallback(async () => {
    setLoading(true)
    try {
      const data = await roleApi.list({ audience })
      setRoles(data.roles || [])
      setSelectedId((current) => {
        const stillThere = (data.roles || []).some((r) => r.id === current)
        return stillThere ? current : (data.roles || [])[0]?.id ?? null
      })
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setLoading(false)
    }
  }, [audience])

  useEffect(() => {
    loadRoles()
  }, [loadRoles])

  useEffect(() => {
    roleApi
      .catalog(audience)
      .then((data) => setCatalog(data.modules || []))
      .catch(() => setCatalog([]))
  }, [audience])

  useEffect(() => {
    if (!selectedId) {
      setDetail(null)
      setDraft({})
      return
    }
    setDetailLoading(true)
    roleApi
      .get(selectedId)
      .then((data) => {
        setDetail(data.role)
        // Action names, not masks. The draft is what the editor toggles and what
        // the server is sent; bits never reach this component.
        setDraft(data.role.permissions || {})
      })
      .catch((err) => setError(err.userMessage))
      .finally(() => setDetailLoading(false))
  }, [selectedId])

  const dirty = useMemo(() => {
    if (!detail) return false
    const current = detail.permissions || {}
    const modules = new Set([...Object.keys(current), ...Object.keys(draft)])
    for (const module of modules) {
      const a = [...(current[module] || [])].sort().join(",")
      const b = [...(draft[module] || [])].sort().join(",")
      if (a !== b) return true
    }
    return false
  }, [detail, draft])

  const toggle = (module, action, checked) => {
    setDraft((current) => {
      const actions = new Set(current[module] || [])
      if (checked) actions.add(action)
      else actions.delete(action)

      const next = { ...current }
      if (actions.size === 0) delete next[module]
      else next[module] = [...actions]
      return next
    })
  }

  const toggleModule = (module, actions, checked) => {
    setDraft((current) => {
      const next = { ...current }
      if (checked) next[module] = actions.map((a) => a.action)
      else delete next[module]
      return next
    })
  }

  const save = async () => {
    setBusy(true)
    setError(null)
    setSaved(null)
    try {
      const result = await roleApi.setPermissions(selectedId, draft)
      setDetail(result.role)
      setDraft(result.role.permissions || {})
      setSaved(
        result.users_affected > 0
          ? `Saved. ${result.users_affected} user${result.users_affected === 1 ? " was" : "s were"} signed out so the change takes effect.`
          : "Saved.",
      )
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setBusy(false)
    }
  }

  const readOnly = !can("rbac_admin", "update")

  return (
    <Box>
      <PageHeader
        title="Roles & Permissions"
        subtitle="What each role may do. A role's audience decides who can hold it — back-office roles belong to the TallyWise team, client roles to a tenant's own people."
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {saved ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(null)}>
          {saved}
        </Alert>
      ) : null}

      <Tabs value={audience} onChange={(_, value) => setAudience(value)} sx={{ mb: 2 }}>
        <Tab value="back_office" label="Back office roles" />
        <Tab value="client" label="Client roles" />
      </Tabs>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-start">
        <Card sx={{ width: { xs: "100%", md: 300 }, flexShrink: 0 }}>
          {loading ? (
            <Box sx={{ p: 2 }}>
              <LoadingRows rows={5} height={44} />
            </Box>
          ) : roles.length === 0 ? (
            <EmptyState icon={ShieldIcon} title="No roles" description="Run the schema seeder to create the predefined roles." />
          ) : (
            <List disablePadding>
              {roles.map((role) => (
                <ListItemButton key={role.id} selected={role.id === selectedId} onClick={() => setSelectedId(role.id)}>
                  <ListItemText
                    primary={role.label}
                    primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                    secondary={`${role.user_count ?? 0} user${role.user_count === 1 ? "" : "s"}`}
                    secondaryTypographyProps={{ fontSize: 12 }}
                  />
                  {role.is_system ? <Chip size="small" label="Predefined" sx={{ height: 20, fontSize: 10 }} /> : null}
                </ListItemButton>
              ))}
            </List>
          )}
        </Card>

        <Card sx={{ flex: 1, width: "100%", minWidth: 0 }}>
          {detailLoading ? (
            <LoadingScreen />
          ) : !detail ? (
            <EmptyState icon={ShieldIcon} title="Select a role" description="Pick a role on the left to see and change what it may do." />
          ) : (
            <Box>
              <Box sx={{ p: 2.5 }}>
                <Typography variant="h2">{detail.label}</Typography>
                {detail.description ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {detail.description}
                  </Typography>
                ) : null}
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={detail.audience === "back_office" ? "Back office" : "Client"} color="primary" variant="outlined" />
                  <Chip size="small" label={detail.data_scope === "assigned" ? "Sees assigned work" : "Sees the whole organization"} variant="outlined" />
                  <Chip size="small" label={`${detail.user_count} user${detail.user_count === 1 ? "" : "s"}`} variant="outlined" />
                </Stack>
              </Box>
              <Divider />

              {/* No nested scroller. A 560px well inside a page that also
                  scrolls means two scrollbars and an action bar that can be
                  below the fold while the grid above it is at its own bottom.
                  The card is as tall as its content; the page scrolls. */}
              <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: "22px" }}>
                {catalog.map((module) => {
                  const selected = new Set(draft[module.module] || [])
                  const allSelected = module.actions.length > 0 && module.actions.every((a) => selected.has(a.action))
                  const someSelected = selected.size > 0 && !allSelected

                  const byGroup = new Map()
                  for (const action of module.actions) {
                    if (!byGroup.has(action.group)) byGroup.set(action.group, [])
                    byGroup.get(action.group).push(action)
                  }
                  const groups = GROUP_ORDER.filter((g) => byGroup.has(g)).concat(
                    [...byGroup.keys()].filter((g) => !GROUP_ORDER.includes(g)),
                  )

                  const grantedHere = module.actions.filter((a) => selected.has(a.action)).length

                  return (
                    <Box key={module.module}>
                      {/* The module header is a rule across the card, so a module
                          reads as a block rather than as another line of
                          checkboxes. The count is the part that makes a fully
                          granted role legible at all: "8 of 8 granted" is one
                          glance, where eight identical ticks is counting. */}
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.25}
                        sx={{ pb: "10px", borderBottom: 1, borderColor: "divider" }}
                      >
                        <FormControlLabel
                          sx={{ mr: 0 }}
                          control={
                            <Checkbox
                              checked={allSelected}
                              indeterminate={someSelected}
                              disabled={readOnly}
                              onChange={(e) => toggleModule(module.module, module.actions, e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="h4">{MODULE_LABELS[module.module] || titleCase(module.module)}</Typography>
                          }
                        />
                        <Box sx={{ flex: 1 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                          {grantedHere} of {module.actions.length} granted
                        </Typography>
                      </Stack>

                      {/* The caption sits in a fixed column beside its actions
                          rather than above them. Repeated under every module with
                          nothing anchoring it, BASICS / DATA / MANAGEMENT was
                          eight identical headings down the card and a module's
                          shape could not be read without counting boxes. */}
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mt: "12px" }}>
                        {groups.map((group) => (
                          <Box
                            key={group}
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", sm: "118px 1fr" },
                              gap: "12px",
                              alignItems: "start",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", pt: "3px" }}
                            >
                              {(GROUP_LABELS[group] || titleCase(group)).toUpperCase()}
                            </Typography>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
                                gap: "8px 12px",
                              }}
                            >
                              {byGroup.get(group).map((action) => (
                                <FormControlLabel
                                  key={action.action}
                                  sx={{ mr: 0, minWidth: 0 }}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={selected.has(action.action)}
                                      disabled={readOnly}
                                      onChange={(e) => toggle(module.module, action.action, e.target.checked)}
                                    />
                                  }
                                  label={
                                    ACTION_HELP[module.module]?.[action.action] ? (
                                      <Tooltip title={ACTION_HELP[module.module][action.action]} placement="top-start">
                                        <Box sx={{ minWidth: 0 }}>
                                          <Typography variant="body2" noWrap>
                                            {titleCase(action.action)}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.3 }}>
                                            {ACTION_HELP[module.module][action.action]}
                                          </Typography>
                                        </Box>
                                      </Tooltip>
                                    ) : (
                                      <Typography variant="body2" noWrap>{titleCase(action.action)}</Typography>
                                    )
                                  }
                                />
                              ))}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )
                })}
              </Box>

              {!readOnly ? (
                <>
                  <Divider />
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 2.5 }}>
                    <Button variant="contained" disabled={!dirty || busy} onClick={save}>
                      {busy ? "Saving…" : "Save permissions"}
                    </Button>
                    <Button color="inherit" disabled={!dirty || busy} onClick={() => setDraft(detail.permissions || {})}>
                      Discard
                    </Button>
                    {dirty && detail.user_count > 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        Saving signs out the {detail.user_count} user{detail.user_count === 1 ? "" : "s"} holding this role, so the
                        change takes effect immediately.
                      </Typography>
                    ) : null}
                  </Stack>
                </>
              ) : null}
            </Box>
          )}
        </Card>
      </Stack>
    </Box>
  )
}
